import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { X } from "lucide-react";

export default function EmailChangeModal({ open, onClose, initialEmail = "" }) {
  const [step, setStep] = useState(1); // 1: new email, 2: otp
  const [newEmail, setNewEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const API = import.meta.env.VITE_BACKEND_URL;

  useEffect(() => {
    if (!open) {
      setStep(1);
      setNewEmail("");
      setOtp("");
      setLoading(false);
      setError("");
      setMessage("");
    }
  }, [open]);

  const handleRequestOtp = async () => {
    setError("");
    setMessage("");
    if (!newEmail) {
      setError("Please enter a new email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/profile/request-email-change`,
        { newEmail },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          withCredentials: true,
        }
      );
      setStep(2);
      setMessage("OTP sent to your new email address.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setMessage("");
    if (!otp) {
      setError("Please enter the OTP sent to your new email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/profile/verify-email-change`,
        { otp, newEmail },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          withCredentials: true,
        }
      );
      setMessage("Email updated! Please log in with your new email.");
      setTimeout(() => {
        localStorage.removeItem("token");
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to verify OTP or update email."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-card rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col mx-4 sm:px-6">
        <div className="sticky top-0 bg-card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Change Email</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="flex flex-col items-stretch justify-center h-full mx-1">
              <p className="text-sm text-foreground mb-4 text-left">
                Enter your new email address to receive an OTP.
              </p>
              {(error || message) && (
                <div className="w-full mb-2">
                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {message && (
                    <div className="text-green-600 text-sm">{message}</div>
                  )}
                </div>
              )}
              <input
                type="email"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-4"
                placeholder="Enter new email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                className="w-full px-4 py-2 mt-3 bg-foreground text-background rounded-md shadow-md hover:bg-foreground/90 transition-all text-sm font-medium disabled:opacity-60"
                onClick={handleRequestOtp}
                disabled={loading || !newEmail}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </div>
          )}
          {step === 2 && (
            <div className="flex flex-col items-stretch justify-center h-full mx-1">
              <p className="text-sm text-foreground mb-4 text-left">
                Enter the OTP sent to your new email address.
              </p>
              {(error || message) && (
                <div className="w-full mb-2">
                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {message && (
                    <div className="text-green-600 text-sm">{message}</div>
                  )}
                </div>
              )}
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-4"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md shadow-md hover:bg-green-700 transition-all text-sm font-medium disabled:opacity-60"
                onClick={handleVerifyOtp}
                disabled={loading || !otp}
              >
                {loading ? "Verifying..." : "Verify & Change Email"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
